import { getLogger } from '@logtape/logtape';
import type { DataClient } from './contract';

const log = getLogger(['safesystem', 'safecurves', 'data']);

// A remote `DataClient` implementation would be chosen here.
async function makeClient(): Promise<DataClient> {
	// Imported here, not at module top, because loading `local/db` opens (and so creates) the SQLite file.
	const [{ LocalSqliteClient }, { boot, resetLocalDb, DbMigrationError }] = await Promise.all([
		import('./local/client'),
		import('./local/reset')
	]);
	try {
		await boot();
	} catch (error) {
		// Only a failed migration means the local DB can't be used as-is; rebuild for that, but surface anything else rather than destroying user data.
		if (!(error instanceof DbMigrationError)) {
			log.error('Local DB boot failed; not rebuilding: {error}', { error });
			throw error;
		}
		log.warn('Rebuilding local DB from scratch, because migration failed: {error}', { error });
		await resetLocalDb();
	}
	return new LocalSqliteClient();
}

// SQLocal keeps the database at the root of the origin's OPFS under this name.
export const DB_FILE = 'safecurves.sqlite3';

// Whether this device has a Programs database, checked without creating one (unlike `getDataClient`).
export async function localDatabaseExists(): Promise<boolean> {
	try {
		await (await navigator.storage.getDirectory()).getFileHandle(DB_FILE);
		return true;
	} catch {
		return false;
	}
}

let persistAsked = false;

// Asks once per session for this origin's storage (the database) to be exempt from automatic eviction; browsers may refuse, and Firefox prompts.
export async function requestPersistentStorage(): Promise<boolean> {
	if (persistAsked || !navigator.storage?.persist) return false;
	persistAsked = true;
	try {
		if (await navigator.storage.persisted()) return true;
		const granted = await navigator.storage.persist();
		log.info('Persistent storage {result}', { result: granted ? 'granted' : 'refused' });
		return granted;
	} catch (error) {
		log.warn('Persistent storage request failed: {error}', { error });
		return false;
	}
}

let client: Promise<DataClient> | undefined;

// Boots the local DB on first use, so it only exists for users who open Programs; a failed boot can be retried.
export function getDataClient(): Promise<DataClient> {
	client ??= makeClient().catch((error: unknown) => {
		client = undefined;
		throw error;
	});
	return client;
}

// The on-device SQLite database as a File, booting it first so the export is never of an empty DB.
export async function exportDatabaseFile(): Promise<File> {
	await getDataClient();
	const { exportDatabaseFile } = await import('./local/reset');
	return exportDatabaseFile();
}

// Checks a database file for import and counts its programs, and this device's, per version.
export async function inspectDatabaseImport(file: File) {
	await getDataClient();
	const { inspectDatabaseFile } = await import('./local/reset');
	return inspectDatabaseFile(file);
}

// Replaces the on-device database with the file; the caller reloads so no store keeps the old data.
export async function importDatabase(file: File): Promise<void> {
	await getDataClient();
	const { importDatabaseFile } = await import('./local/reset');
	await importDatabaseFile(file);
}

// Dev tool: deletes and rebuilds the on-device SQLite database.
export async function resetDatabase(): Promise<void> {
	await getDataClient();
	const { resetLocalDb } = await import('./local/reset');
	await resetLocalDb();
	log.info('Reset local DB');
}
